import { and, eq, type InitialQueryBuilder, isNull } from "@tanstack/db";

import type { WsTemplate } from "@storyboard/web-client";
import { store } from "@/store/store";
import type { Template } from "@/store/types";

function transformWsTemplate(r: WsTemplate): Template {
  return {
    id: r.id,
    name: r.name,
    template: r.template,
    workspaceId: r.workspaceId,
    authorId: r.authorId || "",
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? null,
    deletedAt: r.deletedAt || null,
  };
}

export async function deleteTemplateById(id: string): Promise<void> {
  store.delete("template", [id]);
}

export async function bulkUpsertTemplate(records: WsTemplate[]) {
  if (records.length === 0) return;
  // The old ON CONFLICT SET kept the first-seen createdAt.
  store.upsert("template", records.map(transformWsTemplate), (existing, next) => ({
    ...existing,
    name: next.name,
    template: next.template,
    workspaceId: next.workspaceId,
    authorId: next.authorId,
    updatedAt: next.updatedAt,
    deletedAt: next.deletedAt,
  }));
}

export type CachedTemplateRow = Template & { authorName: string | null; role: string };

export const cachedTemplatesQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ template: store.collection("template") })
    .innerJoin({ account: store.collection("account") }, ({ template, account }) => eq(template.authorId, account.id))
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ account, membership }) =>
      eq(account.id, membership.accountId),
    )
    .where(({ template, membership }) =>
      and(
        eq(template.workspaceId, workspaceId),
        isNull(template.deletedAt),
        eq(membership.workspaceId, workspaceId),
        // One row per template: an author with a stale plus re-invited membership would
        // otherwise fan out to duplicate template rows (and duplicate React keys).
        eq(membership.status, "active"),
      ),
    )
    .select(({ template, account, membership }) => ({
      id: template.id,
      name: template.name,
      template: template.template,
      workspaceId: template.workspaceId,
      authorId: template.authorId,
      createdAt: template.createdAt,
      updatedAt: template.updatedAt,
      deletedAt: template.deletedAt,
      authorName: account.name,
      role: membership.role,
    }))
    .distinct()
    .orderBy(({ template }) => template.createdAt, { direction: "desc", nulls: "first" });
